import { AddCircleOutlineOutlined, ArrowBack, ChevronRight, Person, SupervisedUserCircleOutlined, Update } from "@mui/icons-material";
import { Avatar, Box, Button, IconButton, Table, TableBody, TableContainer, TableRow, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import { BeneficiaryListData } from "../../../../utils/dto/module/beneficiary.type";
import { FetchBeneficiaryListService } from "../../../../utils/services/beneficiary.service";
import { setModuleRefresh } from "../../../../redux/reducer/refreshSlice";
import { openDrawer } from "../../../../redux/reducer/drawerSlice";
import { openDialog } from "../../../../redux/reducer/dialogSlice";
import NoPermissionMessage from "../../../error/Error403";
import PageContent from "../../../../components/common/page/PageContent";
import PageHead from "../../../../components/common/page/PageHead";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import TableHeadList from "../../../../components/common/table/TableHeadList";
import CommonTableCell from "../../../../components/common/table/CommonTableCell";
import { NAVIGATE_ADMIN } from "../../../../constant";
import { generateRandomColor } from "../../../../constant/commonFunctions";
import NoDataFound from "../../../../components/common/table/NoDataFoundModule";
import TableSkeleton from "../../../../components/loader/TableSkeleton";
import CustomPagination from "../../../../components/common/table/TablePagination";


interface HeadCell {
    id: string;
    label: string;
    numeric: boolean;
    width: string;
    isFilterable: boolean;
    isSorting: boolean;
}

const headCells: HeadCell[] = [
    { id: "token_no", label: "Token No", numeric: false, width: "140px", isFilterable: false, isSorting: false },
    { id: "name", label: "Name", numeric: false, width: "200px", isFilterable: false, isSorting: false },
    { id: "Adhar No", label: "Adhaar Number", numeric: false, width: "200px", isFilterable: false, isSorting: false },
    { id: "phone", label: "Phone", numeric: false, width: "150px", isFilterable: false, isSorting: false },
    { id: "Taluka", label: "Taluka", numeric: false, width: "200px", isFilterable: false, isSorting: false },
    { id: "District", label: "District", numeric: false, width: "150px", isFilterable: false, isSorting: false },
    { id: "date_visited", label: "Date Registered", numeric: false, width: "150px", isFilterable: false, isSorting: false },
    { id: "action", label: "Action", numeric: false, width: "120px", isFilterable: false, isSorting: false },
];

const DashboardListingPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const statusParam = searchParams.get('status');
    const status = statusParam === 'pre-registered' || statusParam === 'registered' ? statusParam : 'all';

    const { refresh } = useSelector((state: RootState) => state.sorting);
    const { beneficiaries: moduleRefresh } = useSelector((state: RootState) => state.refresh);
    const { searchTerm, startDate, endDate } = useSelector((state: RootState) => state.filters);

    // const { checkPermission } = usePermissions();

    const isPreRegisterPage: boolean = (status === 'pre-registered');

    const permissionSlug = isPreRegisterPage ? 'view-pre-registration' : 'view-counter-registration';
    const isViewThisPage: boolean = true;

    const isAddBeneficiary: boolean = true;

    const [loading, setLoading] = useState<boolean>(false);
    const [beneficiaries, setBeneficiaries] = useState<BeneficiaryListData[]>([]);
    const [page, setPage] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(15);
    const [totalItems, setTotalItems] = useState<number>(0);

    const fetchBeneficiariesData = useCallback(async () => {
        if (!isViewThisPage) return;

        setLoading(true);
        const payload: any = {
            limit: rowsPerPage,
            flag: status,
            offset: (page - 1) * rowsPerPage,
        };

        const { code, data } = await FetchBeneficiaryListService(payload);
        if (code === 200 && data?.data) {
            const updatedBeneficiaries = data.data.map((beneficiary: BeneficiaryListData) => {
                const displayToken = beneficiary.campaigns?.[0]?.token_no || "N/A";
                const displayStatus = beneficiary.campaigns?.[0]?.status || "N/A";

                return {
                    ...beneficiary,
                    token_no: displayToken,
                    camp_uuid: beneficiary.campaigns?.[0]?.campaign_uuid ?? "N/A",
                    application_status: displayStatus,
                };
            });

            setBeneficiaries(updatedBeneficiaries);
            setTotalItems(data.total_count);
            dispatch(setModuleRefresh({ moduleName: 'beneficiaries', refresh: false }));
        } else {
            setBeneficiaries([]);
            setTotalItems(0);
        }
        setLoading(false);
    }, [dispatch, page, rowsPerPage, status, searchTerm, startDate, endDate, isViewThisPage]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: any) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    const handleAddBeneficiary = useCallback(() => {
        dispatch(openDrawer({
            _Key: "addBeneficiaryDetails",
            title: "Add Beneficiary",
            drawerWidth: "md",
            isFullScreen: false,
            drawerData: { uuid: "", status }
        }));
    }, [status, dispatch]);

    const formatAadhaarNumber = (aadhaar: string | undefined): string => {
        return aadhaar ? aadhaar.replace(/(\d{4})(\d{4})(\d{4})/, "$1-$2-$3") : "--";
    };

    const handleStatusUpdateClick = (user: any) => {
        dispatch(openDialog({
            type: 'markAsVisited',
            isFullScreen: false,
            title: 'Confirm',
            size: 'sm',
            payload: user
        }));
    }

    useEffect(() => {
        if (isViewThisPage) {
            fetchBeneficiariesData();
        }
    }, [moduleRefresh, refresh, fetchBeneficiariesData, isViewThisPage]);

    let Title = "All";
    if (status === 'pre-registered') Title = "Pre-Registered";
    else if (status === 'registered') Title = "Registered";

    if (!isViewThisPage) {
        return <NoPermissionMessage />
    }

    return (
        <PageContent>
            <PageHead
                primary={`${Title} Beneficiary List`}
                secondary={
                    <>
                        {isAddBeneficiary && isPreRegisterPage && (
                            <Button
                                color="primary"
                                variant="contained"
                                startIcon={<AddCircleOutlineOutlined />}
                                onClick={handleAddBeneficiary}
                            >
                                Add Beneficiary
                            </Button>
                        )}
                    </>
                }
                back={
                    <IconButton onClick={()=>{navigate(-1)}}><ArrowBack/></IconButton>
                }
            />
            <Box sx={{ backgroundColor: "background.paper", height: "100%", mt: 1 }}>
                {/* <SearchFilterBar totalItems={totalItems} /> */}
                <TableContainer sx={{ minHeight: 'calc(80vh - 70px)', maxHeight: 'calc(80vh - 70px)' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 750, }}>
                        <TableHeadList headCells={headCells} />
                        <TableBody>
                            {!loading && beneficiaries.map((user) => (
                                <TableRow key={user.id} hover>
                                    <CommonTableCell value={
                                        <Typography onClick={() => navigate(`${NAVIGATE_ADMIN.BENEFICIARY_DETAIL_PAGE}?uuid=${user.uuid}`)} sx={{ color: 'blue', fontWeight: 500, cursor: 'pointer' }} >
                                            {user.token_no}</Typography>} style={{ textAlign: "center" }} />
                                    <CommonTableCell
                                        value={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Avatar sx={{ bgcolor: generateRandomColor(), width: 30, height: 30 }}>
                                                    <Person fontSize="small" />
                                                </Avatar>
                                                <span style={{ fontWeight: 500 }}>{user.name ?? "--"}</span>
                                            </Box>
                                        }
                                    />
                                    <CommonTableCell
                                        value={
                                            <Tooltip title={user.adhar_number ?? ""}>
                                                <span>{formatAadhaarNumber(user.adhar_number)}</span>
                                            </Tooltip>
                                        }
                                    />
                                    <CommonTableCell value={user.mobile_no ?? "--"} />

                                    <CommonTableCell value={user.taluka || "--"} />

                                    <CommonTableCell
                                        value={
                                            <Tooltip title={user.district ?? ""}>
                                                <span>{user.district ?? "--"}</span>
                                            </Tooltip>
                                        }
                                    />
                                    <CommonTableCell value={user.created_at ?? "--"} formatDate style={{ textAlign: "center" }} />
                                    <CommonTableCell
                                        value={<>
                                            <Button size="small" variant="outlined" onClick={() => navigate(`${NAVIGATE_ADMIN.BENEFICIARY_DETAIL_PAGE}?uuid=${user.uuid}`)} endIcon={<ChevronRight />} > View Profile </Button>
                                            {isPreRegisterPage && <Button sx={{
                                                ml: 0.5,
                                            }} size="small" variant="outlined" onClick={() => handleStatusUpdateClick(user)} endIcon={<Update />}>Update Status</Button>}
                                        </>
                                        }
                                        style={{ textAlign: "center" }}
                                    />
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {loading && <TableSkeleton columns={headCells.length} rows={14} />}
                    {!loading && beneficiaries.length === 0 && <NoDataFound height="50vh" moduleName="Beneficiary List" />}
                </TableContainer>
                <CustomPagination
                    count={totalItems}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[15, 20, 30, 50]}
                />
            </Box>
        </PageContent>
    );
};

export default DashboardListingPage;
